import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calculator,
  Download,
  Calendar,
  Users,
  DollarSign,
  Search,
  Filter,
  Plus,
  MessageSquare,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import ExtrasModal from "../components/ExtrasModal";
import SalaryEditModal from "../components/SalaryEditModal";
import EmployeeModal from "../components/EmployeeModal";
import "../styles/Salaries.css";

const Salaries = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlMonth = searchParams.get("month");
  const [salaries, setSalaries] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [presenceMonth, setPresenceMonth] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [selectedPaidStatus, setSelectedPaidStatus] = useState(""); // "", "paid", "unpaid"
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [contractorDropdownOpen, setContractorDropdownOpen] = useState(false);
  const [availableContractors, setAvailableContractors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingValues, setEditingValues] = useState({}); // Track editing values
  const [editingToPay, setEditingToPay] = useState({}); // Track to_pay editing
  const [editingNotes, setEditingNotes] = useState({}); // Track notes editing
  const [hoveredNote, setHoveredNote] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [notesPopupOpen, setNotesPopupOpen] = useState(false);
  const [notesPopupData, setNotesPopupData] = useState(null);
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [salaryEditModalOpen, setSalaryEditModalOpen] = useState(false);
  const [editingSalaryRecord, setEditingSalaryRecord] = useState(null);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const siteDropdownRef = useRef(null);

  useEffect(() => {
    // If URL month is provided, use it directly without fetching available months
    if (urlMonth) {
      setSelectedMonth(urlMonth);
    } else {
      loadAvailableMonths();
    }
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadSalaries();
      loadSummary();
      // Load available months in background for the dropdown (only if not already loaded)
      if (availableMonths.length === 0) {
        loadAvailableMonthsForDropdown();
      }
    }
  }, [selectedMonth]);

  const typeDropdownRef = useRef(null);
  const customerDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        siteDropdownRef.current &&
        !siteDropdownRef.current.contains(event.target)
      ) {
        setSiteDropdownOpen(false);
      }
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target)
      ) {
        setTypeDropdownOpen(false);
      }
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadAvailableMonthsForDropdown = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      if (allMonths.length > 0) {
        setAvailableMonths(allMonths);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadAvailableMonths = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      if (allMonths.length > 0) {
        setAvailableMonths(allMonths);
        setSelectedMonth(allMonths[0]);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const loadSalaries = async () => {
    try {
      setLoading(true);

      // Load all salaries from company_salaries table (unified table for all employee types)
      const response = await apiService.getCompanySalaries(
        selectedMonth,
        user?.partnership_id || 1,
      );

      const allSalaries = response.success ? response.data || [] : [];

      // Filter only קבלן employees and sort by name
      const contractorSalaries = allSalaries
        .filter((s) => s.employee_type === "קבלן")
        .sort((a, b) =>
          (a.employee_name || "").localeCompare(b.employee_name || "", "he"),
        );

      setSalaries(contractorSalaries);

      // Extract unique customers for filter dropdown (from contractor salaries only)
      const uniqueCustomers = [
        ...new Set(
          contractorSalaries.map((s) => s.customer_name).filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b, "he"));

      setCustomers(uniqueCustomers);

      // Extract unique contractors for filter dropdown
      const uniqueContractors = [
        ...new Set(contractorSalaries.map((s) => s.contractor).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "he"));

      setAvailableContractors(uniqueContractors);
    } catch (error) {
      console.error("Failed to load salaries:", error);
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      // Load summary from unified company_salaries table
      const response = await apiService.getCompanySalarySummary(
        selectedMonth,
        user?.partnership_id || 1,
      );

      setSummary(response);
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  };

  const handleCalculateSalaries = async () => {
    if (!selectedMonth) {
      alert("אנא בחר חודש משכורת");
      return;
    }

    // Ask for password confirmation (only for contractor salaries)
    const password = presenceMonth
      ? window.prompt(
          `⚠️ פעולה רגישה: חישוב משכורות\n` +
            `חודש משכורת: ${formatMonth(selectedMonth)}\n` +
            `חודש נוכחות: ${formatMonth(presenceMonth)}\n\n` +
            `אנא הזן את הסיסמה שלך לאישור:`,
          "",
        )
      : null;

    if (presenceMonth && !password) {
      return; // User cancelled
    }

    try {
      setCalculating(true);
      let results = { created: 0, updated: 0, skipped: 0 };

      // Always import company salaries from Excel (חברה and קבלן ו חברה)
      const companyResponse = await apiService.calculateCompanySalaries(
        selectedMonth,
        user?.partnership_id || 1,
      );
      if (companyResponse.success) {
        results.created += companyResponse.created || 0;
        results.updated += companyResponse.updated || 0;
        results.skipped += companyResponse.skipped || 0;
      }

      // Calculate contractor salaries only if presence month is selected
      if (presenceMonth && password) {
        const contractorResponse = await apiService.calculateSalaries(
          selectedMonth,
          presenceMonth,
          password,
        );
        if (contractorResponse.success) {
          results.created += contractorResponse.created || 0;
          results.updated += contractorResponse.updated || 0;
          results.skipped += contractorResponse.skipped || 0;
        }
      }

      alert(
        `✅ ייבוא/חישוב משכורות הושלם:\n` +
          `נוצרו: ${results.created}\n` +
          `עודכנו: ${results.updated}\n` +
          `דולגו: ${results.skipped}`,
      );
      // Reload data after calculation
      await loadSalaries();
      await loadSummary();
    } catch (error) {
      console.error("Failed to calculate salaries:", error);
      const errorMessage =
        error.response?.data?.error || "שגיאה בחישוב משכורות";
      alert(`❌ ${errorMessage}`);
    } finally {
      setCalculating(false);
    }
  };

  const handleUpdateToPay = async (salary) => {
    const newValue = editingToPay[salary.id];

    if (!newValue || isNaN(newValue)) {
      alert("אנא הזן סכום תקין");
      return;
    }

    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך לשנות את סה"כ לתשלום עבור ${
        salary.employee_name
      }?\n\nמ-₪${parseFloat(salary.to_pay).toFixed(2)} ל-₪${parseFloat(
        newValue,
      ).toFixed(2)}`,
    );

    if (!confirmed) {
      setEditingToPay((prev) => {
        const updated = { ...prev };
        delete updated[salary.id];
        return updated;
      });
      return;
    }

    try {
      const data = { to_pay: parseFloat(newValue) };

      // Determine if it's a company salary
      const isCompanySalary = salary?.employee_type?.includes("חברה");

      // Use appropriate API endpoint
      const response = isCompanySalary
        ? await apiService.updateCompanySalary(salary.id, data)
        : await apiService.updateSalary(salary.id, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((s) =>
            s.id === salary.id ? { ...s, to_pay: parseFloat(newValue) } : s,
          ),
        );
        setEditingToPay((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
        await loadSummary();
        alert('✅ סה"כ לתשלום עודכן בהצלחה');
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update to_pay:", error);
      alert('שגיאה בעדכון סה"כ לתשלום');
    }
  };

  const handleUpdateNotes = async (salary) => {
    const newNotes = editingNotes[salary.id];

    try {
      const data = { notes: newNotes || "" };

      // All salaries from companies Excel are now in company_salaries table
      // Only TM salaries use the regular salaries table
      const isCompanySalary =
        salary?.employee_type?.includes("חברה") ||
        salary?.employee_type === "קבלן" ||
        salary?.employee_type === "קבלן ו חברה";

      // Use appropriate API endpoint
      const response = isCompanySalary
        ? await apiService.updateCompanySalary(salary.id, data)
        : await apiService.updateSalary(salary.id, data);

      if (response.success) {
        setSalaries((prevSalaries) =>
          prevSalaries.map((s) =>
            s.id === salary.id ? { ...s, employee_notes: newNotes || "" } : s,
          ),
        );
        setEditingNotes((prev) => {
          const updated = { ...prev };
          delete updated[salary.id];
          return updated;
        });
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update notes:", error);
      alert("שגיאה בעדכון הערות");
    }
  };

  const handleSalaryEditModalUpdate = async (salary, updates) => {
    try {
      // All salaries from companies Excel are now in company_salaries table
      // Only TM salaries use the regular salaries table
      const isCompanySalary =
        salary?.employee_type?.includes("חברה") ||
        salary?.employee_type === "קבלן" ||
        salary?.employee_type === "קבלן ו חברה";

      // Update to_pay
      const toPayData = { to_pay: updates.to_pay };
      const toPayResponse = isCompanySalary
        ? await apiService.updateCompanySalary(salary.id, toPayData)
        : await apiService.updateSalary(salary.id, toPayData);

      if (!toPayResponse.success) {
        throw new Error(toPayResponse.error || "עדכון סכום נכשל");
      }

      // Update notes
      const notesData = { notes: updates.notes || "" };
      const notesResponse = isCompanySalary
        ? await apiService.updateCompanySalary(salary.id, notesData)
        : await apiService.updateSalary(salary.id, notesData);

      if (!notesResponse.success) {
        throw new Error(notesResponse.error || "עדכון הערות נכשל");
      }

      // Update local state
      setSalaries((prevSalaries) =>
        prevSalaries.map((s) =>
          s.id === salary.id
            ? {
                ...s,
                to_pay: updates.to_pay,
                employee_notes: updates.notes || "",
              }
            : s,
        ),
      );

      await loadSummary();
      alert("✅ הנתונים עודכנו בהצלחה");
    } catch (error) {
      console.error("Failed to update salary:", error);
      throw error;
    }
  };

  const handleUpdateSalary = async (salaryId, field, value) => {
    try {
      const data = { [field]: value };

      // Find the salary to determine if it's contractor or company
      const salary = salaries.find((s) => s.id === salaryId);
      // All salaries from companies Excel are now in company_salaries table
      // Only TM salaries use the regular salaries table
      const isCompanySalary =
        salary?.employee_type?.includes("חברה") ||
        salary?.employee_type === "קבלן" ||
        salary?.employee_type === "קבלן ו חברה";

      // Use appropriate API endpoint
      const response = isCompanySalary
        ? await apiService.updateCompanySalary(salaryId, data)
        : await apiService.updateSalary(salaryId, data);

      if (response.success) {
        // Update only the specific row in state instead of reloading everything
        setSalaries((prevSalaries) =>
          prevSalaries.map((salary) =>
            salary.id === salaryId ? { ...salary, [field]: value } : salary,
          ),
        );

        // Only reload summary if it's a field that affects totals
        if (field === "paid" || field === "extras" || field === "subtract") {
          await loadSummary();
        }
      } else {
        alert(`שגיאה: ${response.error || "עדכון נכשל"}`);
      }
    } catch (error) {
      console.error("Failed to update salary:", error);
      alert("שגיאה בעדכון משכורת");
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const openEmployeeModal = async (salary) => {
    try {
      const response = await apiService.getEmployee(salary.employee_id);
      const employee = response.employee || response.data || response;
      setSelectedEmployee(employee);
    } catch (error) {
      console.error("Failed to load employee:", error);
      setSelectedEmployee({
        employee_id: salary.employee_id,
        employee_name: salary.employee_name,
      });
    }
    setEmployeeModalOpen(true);
  };

  const exportToExcel = async () => {
    try {
      // Call backend API to generate Excel with all salaries grouped by site
      const blob = await apiService.exportSalariesToExcel(
        selectedMonth,
        user?.partnership_id || 1,
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `salaries_${selectedMonth.replace("-", "_")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      alert("שגיאה בייצוא לאקסל");
    }
  };

  const exportGoldTM = async () => {
    try {
      const blob = await apiService.exportGoldTM(
        selectedMonth,
        user?.partnership_id || 2,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `gold_tm_${selectedMonth.replace("-", "_")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Gold TM export failed:", error);
      alert("שגיאה בייצוא גולד קלין");
    }
  };

  const exportToExcelOld = async () => {
    // Old export function - keeping for reference
    const XLSX = await import("xlsx");

    const filteredSalaries = getFilteredSalaries();

    const headers = [
      "שם עובד",
      "לקוח",
      "אתר",
      "סוג עובד",
      "שעות רגילות",
      "שעות סופ״ש",
      "סה״כ שעות",
      "תעריף רגיל",
      "תעריף סופ״ש",
      "משכורת",
      "תוספות",
      "סה״כ לתשלום",
      "שולם",
    ];

    const rows = filteredSalaries.map((item) => [
      item.employee_name || "",
      item.customer_name || "",
      item.site_name || "",
      item.employee_type || "",
      item.regular_hours?.toFixed(2) || "0.00",
      item.weekend_hours?.toFixed(2) || "0.00",
      item.total_hours?.toFixed(2) || "0.00",
      item.hour_rate?.toFixed(2) || "",
      item.weekend_hour_rate?.toFixed(2) || "",
      item.salary?.toFixed(2) || "0.00",
      item.extras?.toFixed(2) || "0.00",
      item.total_to_pay?.toFixed(2) || "0.00",
      item.paid ? "כן" : "לא",
    ]);

    // Create worksheet from array of arrays
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    ws["!cols"] = [
      { wch: 20 }, // שם עובד
      { wch: 20 }, // לקוח
      { wch: 20 }, // אתר
      { wch: 15 }, // סוג עובד
      { wch: 12 }, // שעות רגילות
      { wch: 12 }, // שעות סופ״ש
      { wch: 12 }, // סה״כ שעות
      { wch: 12 }, // תעריף רגיל
      { wch: 12 }, // תעריף סופ״ש
      { wch: 12 }, // משכורת
      { wch: 12 }, // תוספות
      { wch: 12 }, // סה״כ לתשלום
      { wch: 10 }, // שולם
    ];

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, formatMonth(selectedMonth));

    // Write file
    XLSX.writeFile(wb, `salaries_${selectedMonth}.xlsx`);
  };

  const getFilteredSalaries = () => {
    const filtered = salaries.filter((salary) => {
      // For salary_viewer users, only show contractor (קבלן) salaries
      if (
        user?.permission === "salary_viewer" &&
        salary.employee_type !== "קבלן"
      ) {
        return false;
      }

      // Filter by name
      const matchesName =
        searchName === "" ||
        (salary.employee_name || "")
          .toLowerCase()
          .includes(searchName.toLowerCase());

      // Filter by customer (multi-select)
      const matchesCustomer =
        selectedCustomers.length === 0 ||
        selectedCustomers.includes(salary.customer_name);

      // Filter by site (multi-select)
      const matchesSite =
        selectedSites.length === 0 || selectedSites.includes(salary.site_name);

      // Filter by employee type (multi-select)
      const matchesType =
        selectedTypes.length === 0 ||
        selectedTypes.includes(salary.employee_type);

      // Filter by paid status
      const matchesPaidStatus =
        selectedPaidStatus === "" ||
        (selectedPaidStatus === "paid" && salary.paid) ||
        (selectedPaidStatus === "unpaid" && !salary.paid);

      // Filter by contractor (multi-select)
      const matchesContractor =
        selectedContractors.length === 0 ||
        selectedContractors.includes(salary.contractor);

      return (
        matchesName &&
        matchesCustomer &&
        matchesSite &&
        matchesType &&
        matchesPaidStatus &&
        matchesContractor
      );
    });

    // Sort: unpaid first, paid at bottom
    return filtered.sort((a, b) => {
      if (a.paid === b.paid) return 0;
      return a.paid ? 1 : -1;
    });
  };

  const toggleSiteSelection = (site) => {
    setSelectedSites((prev) =>
      prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site],
    );
  };

  const clearSiteSelection = () => {
    setSelectedSites([]);
    setSiteDropdownOpen(false);
  };

  const toggleTypeSelection = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const clearTypeSelection = () => {
    setSelectedTypes([]);
    setTypeDropdownOpen(false);
  };

  const toggleCustomerSelection = (customer) => {
    setSelectedCustomers((prev) =>
      prev.includes(customer)
        ? prev.filter((c) => c !== customer)
        : [...prev, customer],
    );
  };

  const clearCustomerSelection = () => {
    setSelectedCustomers([]);
    setCustomerDropdownOpen(false);
  };

  const toggleContractorSelection = (contractor) => {
    setSelectedContractors((prev) =>
      prev.includes(contractor)
        ? prev.filter((c) => c !== contractor)
        : [...prev, contractor],
    );
  };

  const clearContractorSelection = () => {
    setSelectedContractors([]);
    setContractorDropdownOpen(false);
  };

  const getUniqueSites = () => {
    return [
      ...new Set(salaries.map((s) => s.site_name).filter(Boolean)),
    ].sort();
  };

  const handleBulkPayBySites = async (sites) => {
    const unpaidSalaries = salaries.filter(
      (s) => sites.includes(s.site_name) && !s.paid,
    );

    if (unpaidSalaries.length === 0) {
      alert("אין משכורות שלא שולמו באתרים שנבחרו");
      return;
    }

    const siteText =
      sites.length === 1 ? `אתר "${sites[0]}"` : `${sites.length} אתרים`;

    const confirmed = window.confirm(
      `האם לסמן ${unpaidSalaries.length} משכורות כשולמו עבור ${siteText}?`,
    );

    if (!confirmed) return;

    try {
      for (const salary of unpaidSalaries) {
        await apiService.updateSalary(salary.id, { paid: true });
      }
      await loadSalaries();
      await loadSummary();
      alert("המשכורות עודכנו בהצלחה!");
    } catch (error) {
      console.error("Failed to bulk update salaries:", error);
      alert("שגיאה בעדכון משכורות");
    }
  };

  return (
    <div className='salaries-container'>
      <div className='salaries-header'>
        <div>
          <h1>משכורות קבלנים</h1>
          <p>ניהול משכורות עובדי קבלן</p>
        </div>
        <div className='header-actions'>
          <div className='month-selector'>
            <Calendar size={18} />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value=''>בחר חודש משכורת</option>
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
          </div>
          {user?.permission !== "salary_viewer" && (
            <>
              <button
                onClick={exportToExcel}
                className='export-btn'
                disabled={!salaries.length}
                title='ייצא ל-Excel'
              >
                <Download size={18} />
                ייצא ל-Excel
              </button>
              {user?.partnership_id === 2 && (
                <button
                  onClick={exportGoldTM}
                  className='export-btn'
                  disabled={!salaries.length}
                  title='ייצא T&M גולד קלין'
                  style={{ backgroundColor: "#C9A227" }}
                >
                  <Download size={18} />
                  T&M גולד
                </button>
              )}
              {(user?.permission === "admin" ||
                user?.permission === "owner") && (
                <button
                  className='calculate-btn'
                  onClick={handleCalculateSalaries}
                  disabled={!selectedMonth || calculating}
                >
                  <Calculator size={18} />
                  {calculating ? "מחשב..." : "חשב משכורות"}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {summary &&
        (() => {
          // Calculate filtered statistics
          const filteredSalaries = getFilteredSalaries();
          const filteredStats = {
            total_employees: filteredSalaries.length,
            total_to_pay: filteredSalaries.reduce(
              (sum, s) =>
                sum + ((s.to_pay || 0) + (s.extras || 0) - (s.subtract || 0)),
              0,
            ),
            paid_employees: filteredSalaries.filter((s) => s.paid).length,
            paid_amount: filteredSalaries
              .filter((s) => s.paid)
              .reduce(
                (sum, s) =>
                  sum + ((s.to_pay || 0) + (s.extras || 0) - (s.subtract || 0)),
                0,
              ),
          };

          return (
            <div className='summary-cards'>
              <div className='summary-card'>
                <div
                  className='summary-icon'
                  style={{ background: "#dbeafe", color: "#3b82f6" }}
                >
                  <Users size={24} />
                </div>
                <div className='summary-content'>
                  <p className='summary-label'>עובדים</p>
                  <h3 className='summary-value'>
                    {filteredStats.total_employees}
                  </h3>
                </div>
              </div>

              {(user?.permission === "admin" ||
                user?.permission === "owner") && (
                <>
                  <div className='summary-card'>
                    <div
                      className='summary-icon'
                      style={{ background: "#dcfce7", color: "#10b981" }}
                    >
                      <DollarSign size={24} />
                    </div>
                    <div className='summary-content'>
                      <p className='summary-label'>סה״כ לתשלום</p>
                      <h3 className='summary-value'>
                        ₪
                        {filteredStats.total_to_pay.toLocaleString("he-IL", {
                          maximumFractionDigits: 0,
                        })}
                      </h3>
                    </div>
                  </div>
                  <div className='summary-card paid'>
                    <div
                      className='summary-icon'
                      style={{ background: "#fce7f3", color: "#ec4899" }}
                    >
                      <DollarSign size={24} />
                    </div>
                    <div className='summary-content'>
                      <p className='summary-label'>שולם</p>
                      <h3 className='summary-value'>
                        ₪
                        {filteredStats.paid_amount.toLocaleString("he-IL", {
                          maximumFractionDigits: 0,
                        })}
                      </h3>
                      <p className='summary-sublabel'>
                        {filteredStats.paid_employees} עובדים
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className='summary-card remaining'>
                <div
                  className='summary-icon'
                  style={{ background: "#fef3c7", color: "#f59e0b" }}
                >
                  <Users size={24} />
                </div>
                <div className='summary-content'>
                  <p className='summary-label'>עובדים שלא שולמו</p>
                  <h3 className='summary-value'>
                    {filteredStats.total_employees -
                      filteredStats.paid_employees}
                  </h3>
                </div>
              </div>
            </div>
          );
        })()}

      {!loading && salaries.length > 0 && (
        <div className='filters-section'>
          <div className='search-box'>
            <Search size={18} />
            <input
              type='text'
              placeholder='חיפוש לפי שם עובד...'
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          <div
            className='customer-filter multi-select-container'
            ref={customerDropdownRef}
          >
            <Filter size={18} />
            <div
              className='multi-select-trigger'
              onClick={() => setCustomerDropdownOpen(!customerDropdownOpen)}
            >
              {selectedCustomers.length === 0
                ? "כל הלקוחות"
                : `${selectedCustomers.length} לקוחות נבחרו`}
            </div>
            {customerDropdownOpen && (
              <div className='multi-select-dropdown'>
                <div className='multi-select-header'>
                  <span>בחר לקוחות</span>
                  {selectedCustomers.length > 0 && (
                    <button
                      onClick={clearCustomerSelection}
                      className='clear-btn'
                    >
                      נקה הכל
                    </button>
                  )}
                </div>
                <div className='multi-select-options'>
                  {customers.map((customer) => (
                    <label key={customer} className='multi-select-option'>
                      <input
                        type='checkbox'
                        checked={selectedCustomers.includes(customer)}
                        onChange={() => toggleCustomerSelection(customer)}
                      />
                      <span>{customer}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            className='customer-filter multi-select-container'
            ref={typeDropdownRef}
          >
            <Filter size={18} />
            <div
              className='multi-select-trigger'
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
            >
              {selectedTypes.length === 0
                ? "כל הסוגים"
                : `${selectedTypes.length} סוגים נבחרו`}
            </div>
            {typeDropdownOpen && (
              <div className='multi-select-dropdown'>
                <div className='multi-select-header'>
                  <span>בחר סוגים</span>
                  {selectedTypes.length > 0 && (
                    <button onClick={clearTypeSelection} className='clear-btn'>
                      נקה הכל
                    </button>
                  )}
                </div>
                <div className='multi-select-options'>
                  {["קבלן", "חברה", "קבלן ו חברה"].map((type) => (
                    <label key={type} className='multi-select-option'>
                      <input
                        type='checkbox'
                        checked={selectedTypes.includes(type)}
                        onChange={() => toggleTypeSelection(type)}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            className='customer-filter multi-select-container'
            ref={siteDropdownRef}
          >
            <Filter size={18} />
            <div
              className='multi-select-trigger'
              onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
            >
              {selectedSites.length === 0
                ? "כל האתרים"
                : `${selectedSites.length} אתרים נבחרו`}
            </div>
            {siteDropdownOpen && (
              <div className='multi-select-dropdown'>
                <div className='multi-select-header'>
                  <span>בחר אתרים</span>
                  {selectedSites.length > 0 && (
                    <button onClick={clearSiteSelection} className='clear-btn'>
                      נקה הכל
                    </button>
                  )}
                </div>
                <div className='multi-select-options'>
                  {getUniqueSites().map((site) => (
                    <label key={site} className='multi-select-option'>
                      <input
                        type='checkbox'
                        checked={selectedSites.includes(site)}
                        onChange={() => toggleSiteSelection(site)}
                      />
                      <span>{site}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {availableContractors.length > 0 && (
            <div className='multi-select-container' ref={null}>
              <div
                className='multi-select-trigger'
                onClick={() =>
                  setContractorDropdownOpen(!contractorDropdownOpen)
                }
              >
                {selectedContractors.length === 0
                  ? "כל הקבלנים"
                  : `${selectedContractors.length} קבלנים נבחרו`}
              </div>
              {contractorDropdownOpen && (
                <div className='multi-select-dropdown'>
                  <div className='multi-select-header'>
                    <span>בחר קבלנים</span>
                    {selectedContractors.length > 0 && (
                      <button
                        onClick={clearContractorSelection}
                        className='clear-btn'
                      >
                        נקה הכל
                      </button>
                    )}
                  </div>
                  <div className='multi-select-options'>
                    {availableContractors.map((contractor) => (
                      <label key={contractor} className='multi-select-option'>
                        <input
                          type='checkbox'
                          checked={selectedContractors.includes(contractor)}
                          onChange={() => toggleContractorSelection(contractor)}
                        />
                        <span>{contractor}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className='customer-filter'>
            <Filter size={18} />
            <select
              value={selectedPaidStatus}
              onChange={(e) => setSelectedPaidStatus(e.target.value)}
            >
              <option value=''>הכל</option>
              <option value='paid'>שולם</option>
              <option value='unpaid'>לא שולם</option>
            </select>
          </div>
          {(user?.permission === "site_admin" ||
            user?.permission === "admin" ||
            user?.permission === "owner") &&
            selectedSites.length > 0 && (
              <button
                className='bulk-pay-btn'
                onClick={() => handleBulkPayBySites(selectedSites)}
              >
                <DollarSign size={18} />
                {selectedSites.length === 1
                  ? `סמן הכל כשולם - ${selectedSites[0]}`
                  : `סמן הכל כשולם - ${selectedSites.length} אתרים`}
              </button>
            )}
        </div>
      )}

      {loading ? (
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>טוען משכורות...</p>
        </div>
      ) : salaries.length === 0 ? (
        <div className='empty-state'>
          <Calculator size={64} />
          <h3>אין משכורות לחודש זה</h3>
          <p>לחץ על "חשב משכורות" כדי ליצור משכורות עבור עובדים קבלנים</p>
        </div>
      ) : (
        <div className='salaries-table-container'>
          <table className='salaries-table'>
            <thead>
              <tr>
                <th>שולם</th>
                <th>לקוח / אתר</th>
                <th>שם עובד</th>
                <th>שעות רגילות</th>
                <th>שעות סופ״ש</th>
                <th>שכר קבלן</th>
                {(user?.permission === "admin" ||
                  user?.permission === "owner") && (
                  <>
                    <th>תוספות</th>
                    <th>ניכויים</th>
                    <th>פעולות</th>
                  </>
                )}
                <th>סה״כ לתשלום</th>
                <th>הערות</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredSalaries().map((salary, index) => (
                <tr
                  key={`${salary.employee_type}-${salary.id}-${index}`}
                  className={salary.paid ? "paid-row" : ""}
                >
                  <td className='checkbox-cell'>
                    <input
                      type='checkbox'
                      checked={salary.paid || false}
                      onChange={(e) =>
                        handleUpdateSalary(salary.id, "paid", e.target.checked)
                      }
                      disabled={user?.permission === "viewer"}
                    />
                  </td>
                  <td className='customer-site-cell'>
                    <div className='customer-name'>
                      {salary.customer_name || "-"}
                    </div>
                    <div className='site-name'>{salary.site_name || "-"}</div>
                  </td>
                  <td
                    className='employee-name clickable-cell'
                    onClick={() => openEmployeeModal(salary)}
                    title='לחץ לעריכת פרטי עובד'
                  >
                    {salary.employee_name}
                    {/* משכורת גלובלית - Global salary indicator */}
                    {salary.is_global_salary && (
                      <span
                        className='global-salary-badge'
                        title='משכורת גלובלית'
                      >
                        גלובלי
                      </span>
                    )}
                  </td>
                  <td className='hours-cell'>
                    {salary.total_regular_hours.toFixed(2)}
                  </td>
                  <td className='hours-cell'>
                    {salary.total_weekend_hours.toFixed(2)}
                  </td>
                  <td
                    className={`salary-cell ${salary.is_global_salary ? "global-salary" : ""}`}
                  >
                    {salary.salary_cash
                      ? `₪${salary.salary_cash.toLocaleString("he-IL")}`
                      : "-"}
                    {salary.is_global_salary && salary.global_salary_amount && (
                      <div className='global-salary-hint'>משכורת קבועה</div>
                    )}
                  </td>
                  {(user?.permission === "admin" ||
                    user?.permission === "owner") && (
                    <>
                      <td className='extras-cell'>
                        ₪{(salary.extras || 0).toFixed(2)}
                        {salary.extras_details?.items?.length > 0 && (
                          <span className='items-count'>
                            (
                            {
                              salary.extras_details.items.filter(
                                (i) => i.type === "extra",
                              ).length
                            }
                            )
                          </span>
                        )}
                      </td>
                      <td className='deductions-cell'>
                        ₪{(salary.subtract || 0).toFixed(2)}
                        {salary.extras_details?.items?.length > 0 && (
                          <span className='items-count'>
                            (
                            {
                              salary.extras_details.items.filter(
                                (i) => i.type === "deduction",
                              ).length
                            }
                            )
                          </span>
                        )}
                      </td>
                      <td className='actions-cell'>
                        <button
                          className='manage-extras-btn'
                          onClick={() => {
                            if (!salary.paid) {
                              setSelectedSalary(salary);
                              setExtrasModalOpen(true);
                            }
                          }}
                          title={
                            salary.paid
                              ? "לא ניתן לערוך משכורת ששולמה"
                              : "נהל תוספות וניכויים"
                          }
                          disabled={salary.paid}
                        >
                          <Plus size={16} />
                        </button>
                      </td>
                    </>
                  )}
                  <td
                    className={`total-cell ${
                      !salary.paid &&
                      (user?.permission === "admin" ||
                        user?.permission === "owner")
                        ? "clickable-cell"
                        : ""
                    }`}
                    onClick={() => {
                      if (
                        !salary.paid &&
                        (user?.permission === "admin" ||
                          user?.permission === "owner")
                      ) {
                        setEditingSalaryRecord(salary);
                        setSalaryEditModalOpen(true);
                      }
                    }}
                    title={
                      salary.paid
                        ? "לא ניתן לערוך משכורת ששולמה"
                        : user?.permission === "admin" ||
                            user?.permission === "owner"
                          ? "לחץ לעריכה"
                          : ""
                    }
                  >
                    <span>
                      ₪
                      {(
                        (salary.to_pay || 0) +
                        (salary.extras || 0) -
                        (salary.subtract || 0)
                      ).toLocaleString("he-IL", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </td>
                  <td
                    className={`notes-cell ${salary.employee_notes ? "has-notes clickable-cell" : ""}`}
                    onClick={() => {
                      if (salary.employee_notes) {
                        const notesList = salary.employee_notes
                          .split("|")
                          .map((n) => n.trim());
                        setNotesPopupData({
                          employeeName: salary.employee_name,
                          notes: notesList,
                        });
                        setNotesPopupOpen(true);
                      }
                    }}
                    title={salary.employee_notes ? "לחץ לצפייה בהערות" : ""}
                  >
                    {salary.employee_notes ? (
                      <div className='notes-indicator'>
                        <MessageSquare size={16} />
                        <span className='notes-count'>
                          {salary.employee_notes.split("|").length}
                        </span>
                      </div>
                    ) : (
                      <span className='no-notes'>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Extras Modal */}
      <ExtrasModal
        isOpen={extrasModalOpen}
        onClose={() => {
          setExtrasModalOpen(false);
          setSelectedSalary(null);
        }}
        salary={selectedSalary}
        onUpdate={() => {
          loadSalaries();
          loadSummary();
        }}
        currentMonth={selectedMonth}
      />

      {/* Salary Edit Modal */}
      <SalaryEditModal
        isOpen={salaryEditModalOpen}
        onClose={() => {
          setSalaryEditModalOpen(false);
          setEditingSalaryRecord(null);
        }}
        salary={editingSalaryRecord}
        onUpdate={handleSalaryEditModalUpdate}
      />

      {/* Notes Popup Modal */}
      {notesPopupOpen && notesPopupData && (
        <div
          className='notes-popup-overlay'
          onClick={() => setNotesPopupOpen(false)}
        >
          <div
            className='notes-popup-content'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='notes-popup-header'>
              <h3>הערות - {notesPopupData.employeeName}</h3>
              <button
                className='notes-popup-close'
                onClick={() => setNotesPopupOpen(false)}
              >
                ×
              </button>
            </div>
            <div className='notes-popup-list'>
              {notesPopupData.notes.map((note, index) => {
                const isMonthly = note.includes("[חודשי]");
                const noteText = note
                  .replace("[חודשי]", "")
                  .replace("[קבוע]", "")
                  .trim();
                const noteType = isMonthly ? "חודשי" : "קבוע";
                return (
                  <div key={index} className='notes-popup-item'>
                    <div className='notes-popup-item-type'>
                      <span
                        className={`note-type-badge ${isMonthly ? "monthly" : "permanent"}`}
                      >
                        {noteType}
                      </span>
                    </div>
                    <div className='notes-popup-item-text'>{noteText}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Employee Edit Modal */}
      {employeeModalOpen && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={(reload) => {
            setEmployeeModalOpen(false);
            setSelectedEmployee(null);
            if (reload) {
              loadSalaries();
              loadSummary();
            }
          }}
        />
      )}
    </div>
  );
};

export default Salaries;
